import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Button, Flex, Input, Spinner, Text } from '@chakra-ui/react';
import { HiX } from 'react-icons/hi';
import parse from 'html-react-parser';
import { debounce } from '@/utils/helpers';
import {
    useVideoQuizDomains,
    VideoQuizDomainV2,
} from '@/features/Quiz/api/getVideoQuizDomains';

type Props = {
    value: VideoQuizDomainV2 | null;
    onChange: (domain: VideoQuizDomainV2 | null) => void;
};

type DropdownPos = { top: number; left: number; width: number };

const CAP_HINT = 'Showing top 50. Type to find exact match.';

const DomainLabel: React.FC<{ domain: VideoQuizDomainV2 }> = ({ domain }) => (
    <>
        {domain.id} — {parse(domain.title ?? '')}
    </>
);

export const ArticleDomainFilter: React.FC<Props> = ({ value, onChange }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [pos, setPos] = useState<DropdownPos | null>(null);
    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const buttonRef = useRef<HTMLButtonElement>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const searchInputRef = useRef<HTMLInputElement>(null);

    const applySearch = useCallback((keyword: string) => {
        setDebouncedSearch(keyword);
    }, []);

    const onSearchChange = debounce(
        useCallback(
            (e: React.ChangeEvent<HTMLInputElement>) => {
                applySearch(e.target.value);
            },
            [applySearch]
        ),
        300
    );

    const { data, isFetching } = useVideoQuizDomains({
        search: debouncedSearch || undefined,
        size: 50,
        enabled: isOpen,
    });

    const domains = data?.domains ?? [];
    const truncated = data?.truncated ?? false;

    const updatePos = useCallback(() => {
        if (buttonRef.current) {
            const rect = buttonRef.current.getBoundingClientRect();
            setPos({
                top: rect.bottom + window.scrollY + 4,
                left: rect.left + window.scrollX,
                width: Math.max(rect.width, 320),
            });
        }
    }, []);

    const open = useCallback(() => {
        updatePos();
        setIsOpen(true);
        setTimeout(() => searchInputRef.current?.focus(), 0);
    }, [updatePos]);

    const close = useCallback(() => {
        setIsOpen(false);
        setSearch('');
        setDebouncedSearch('');
    }, []);

    const toggle = useCallback(() => {
        if (isOpen) close();
        else open();
    }, [isOpen, open, close]);

    useEffect(() => {
        if (!isOpen) return;
        const handler = (e: MouseEvent) => {
            const target = e.target as Node;
            if (
                buttonRef.current &&
                !buttonRef.current.contains(target) &&
                dropdownRef.current &&
                !dropdownRef.current.contains(target)
            ) {
                close();
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [isOpen, close]);

    useEffect(() => {
        if (!isOpen) return;
        const handler = () => updatePos();
        window.addEventListener('resize', handler);
        window.addEventListener('scroll', handler, true);
        return () => {
            window.removeEventListener('resize', handler);
            window.removeEventListener('scroll', handler, true);
        };
    }, [isOpen, updatePos]);

    const handleSelect = useCallback(
        (domain: VideoQuizDomainV2) => {
            onChange(domain);
            close();
        },
        [onChange, close]
    );

    const handleClear = useCallback(
        (e: React.MouseEvent) => {
            e.stopPropagation();
            onChange(null);
        },
        [onChange]
    );

    const triggerLabel = value ? <DomainLabel domain={value} /> : 'Filter by article...';

    return (
        <Box position="relative" w="320px" flexShrink={0}>
            {/* @ts-expect-error Expression produces a union type that is too complex to represent. */}
            <Button
                ref={buttonRef}
                as="button"
                type="button"
                w="100%"
                h="2.5rem"
                px={3}
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                border="1px solid"
                borderColor="gray.200"
                borderRadius="1000px"
                bg="white"
                fontWeight="normal"
                fontSize="sm"
                _hover={{ bg: 'white' }}
                _active={{ bg: 'white' }}
                onClick={toggle}>
                <Text
                    flex="1"
                    textAlign="left"
                    noOfLines={1}
                    color={value ? 'gray.800' : 'gray.400'}
                    fontSize="sm">
                    {triggerLabel}
                </Text>
                {value ? (
                    <Box
                        as="span"
                        display="flex"
                        alignItems="center"
                        ml={2}
                        color="gray.500"
                        onClick={handleClear}
                        aria-label="Clear article filter">
                        <HiX />
                    </Box>
                ) : (
                    <Box
                        as="span"
                        display="inline-block"
                        w="0"
                        h="0"
                        borderLeft="5px solid transparent"
                        borderRight="5px solid transparent"
                        borderTop={isOpen ? 'none' : '5px solid'}
                        borderBottom={isOpen ? '5px solid' : 'none'}
                        borderTopColor="gray.500"
                        borderBottomColor="gray.500"
                        flexShrink={0}
                        ml={2}
                    />
                )}
            </Button>

            {isOpen && pos && (
                <Box
                    ref={dropdownRef}
                    position="fixed"
                    zIndex={10000}
                    top={pos.top + 'px'}
                    left={pos.left + 'px'}
                    w={pos.width + 'px'}
                    bg="white"
                    border="1px solid"
                    borderColor="gray.200"
                    borderRadius="lg"
                    boxShadow="0 10px 15px -3px rgba(0,0,0,0.10), 0 4px 6px -2px rgba(0,0,0,0.05)"
                    maxH="22rem"
                    display="flex"
                    flexDir="column"
                    overflow="hidden">
                    <Box p={3} pb={2}>
                        <Input
                            ref={searchInputRef}
                            size="sm"
                            placeholder="Search by id or title..."
                            defaultValue={search}
                            onChange={(e) => {
                                setSearch(e.target.value);
                                onSearchChange(e);
                            }}
                            borderRadius="md"
                        />
                    </Box>

                    <Box overflowY="auto" flex="1">
                        {isFetching && (
                            <Flex justify="center" py={4}>
                                <Spinner size="sm" />
                            </Flex>
                        )}
                        {!isFetching && domains.length === 0 && (
                            <Text px={4} py={6} textAlign="center" color="gray.500" fontSize="sm">
                                No articles found.
                            </Text>
                        )}
                        {!isFetching &&
                            domains.map((domain) => {
                                const isSelected = value?.id === domain.id;
                                return (
                                    <Box
                                        key={domain.id}
                                        px={3}
                                        py="0.625rem"
                                        cursor="pointer"
                                        bg={isSelected ? 'blue.50' : 'white'}
                                        _hover={{ bg: isSelected ? 'blue.50' : 'gray.50' }}
                                        borderBottom="1px solid"
                                        borderColor="gray.100"
                                        onClick={() => handleSelect(domain)}>
                                        <Text
                                            fontSize="sm"
                                            fontWeight={isSelected ? 'semibold' : 'normal'}
                                            color={isSelected ? 'blue.600' : 'gray.800'}
                                            noOfLines={2}>
                                            <DomainLabel domain={domain} />
                                        </Text>
                                    </Box>
                                );
                            })}
                    </Box>

                    {(truncated || (!debouncedSearch && domains.length > 0)) && (
                        <Box
                            px={3}
                            py={2}
                            borderTop="1px solid"
                            borderColor="gray.100"
                            bg="gray.50">
                            <Text fontSize="xs" color="gray.500">
                                {CAP_HINT}
                            </Text>
                        </Box>
                    )}
                </Box>
            )}
        </Box>
    );
};
